import React, { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import {
    selectedBookingAtom,
    selectedBookingIdAtom,
    selectedBookingNotesAtom,
    selectedBookingNotesContentAtom
} from "../atoms/bookingsTable";
import { useAtom } from "jotai";
import LooksOneOutlinedIcon from "@mui/icons-material/LooksOneOutlined";
import {
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    FormControlLabel,
    Switch,
    useMediaQuery,
    useTheme
} from "@mui/material";
import ArrowIcon from "../images/ArrowIcon";
import moment from "moment";
import SyncIcon from "../images/SyncIcon";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
    confirmationFunctionAtom,
    conformationDialogContentAtom,
    conformationDialogIsSubmittingAtom,
    conformationDialogOpenAtom,
    conformationDialogSubmitButtonAtom,
    conformationDialogTitleAtom,
    isMbxUserProvider,
    isMbxUserVerifiedStaff,
    mbxUserAtom
} from "../atoms/atoms";
import { bookingPageSnackbarAtom } from "../atoms/bookingsTable";
import { selectedBookingForRescheduleAtom } from "../atoms/providerReschedule";
import { graphql, graphqlOperation } from "../modules/AmplifyServices";
import * as queries from "../graphql/queries";
import * as mutations from "../graphql/mutations";
import LinkDetectionTextField from "../utils/CommonComonents/LinkDetectionTextField";
import { auditBookingState, auditOrderNotesUpdate } from "../modules/Audit";
import NotificationSnackbar from "../components/NotificationSnackbar";
import { userCanCancelAppt } from "../user/UserCommon";
import PackageIcon from "../images/PackageIcon";

export default function ManageBookingDialog({
    open,
    onClose,
    TransitionComponent = null,
    handleOpenVirtualBookingModal,
    updateLocalBooking,
    onSaveNotes,
    handleCancel,
    handleCompleted,
    handleAddCharge,
    handleNoShow,
    handleOverride,
    openRescheduleModal,
    setSelectedRowData
}) {
    const [mbxUser] = useAtom(mbxUserAtom);
    const [isProviderLoggedIn] = useAtom(isMbxUserProvider);
    const [selectedBooking, setSelectedBooking] = useAtom(selectedBookingAtom);
    const [selectedBookingId, setSelectedBookingId] = useAtom(
        selectedBookingIdAtom
    );
    const [, setSelectedBookingForReschedule] = useAtom(
        selectedBookingForRescheduleAtom
    );
    const [isVerifiedStaff] = useAtom(isMbxUserVerifiedStaff);
    const [, setConfirmationDialogContent] = useAtom(
        conformationDialogContentAtom
    );
    const [, setConfirmationFunction] = useAtom(confirmationFunctionAtom);
    const [, setConfirmationDialogOpen] = useAtom(conformationDialogOpenAtom);
    const [, setConformationDialogTitle] = useAtom(conformationDialogTitleAtom);
    const [, setConformationDialogSubmitButton] = useAtom(
        conformationDialogSubmitButtonAtom
    );
    const [, setConformationDialogIsSubmitting] = useAtom(
        conformationDialogIsSubmittingAtom
    );
    const [bookingNotes] = useAtom(selectedBookingNotesAtom);
    const [bookingNotesContent] = useAtom(selectedBookingNotesContentAtom);
    const [applyNotesToOrder, setApplyNotesToOrder] = useState(false);
    const [bookingPageSnackbar, setBookingPageSnackbar] = useAtom(
        bookingPageSnackbarAtom
    );
    const {
        open: openSnackbar = false,
        severity: snackSeverity = "success",
        message: snackMsg = ""
    } = bookingPageSnackbar;

    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"), {
        defaultMatches: true
    });
    const originalBookingNotes = (() => {
        if (!selectedBooking || Object.keys(selectedBooking).length === 0)
            return "";

        const orderNotes = selectedBooking?.order?.Notes
            ? JSON.parse(selectedBooking?.order?.Notes)[0]?.notes
            : null;
        const bookingNotes = selectedBooking?.Notes
            ? selectedBooking.Notes[0]?.notes
            : null;

        return bookingNotes
            ? `${bookingNotes}`
            : orderNotes
              ? `${orderNotes}`
              : "";
    })();

    const [notes, setNotes] = useState(originalBookingNotes);

    const handleClose = () => {
        setSelectedBookingId(null);
        setSelectedBooking(null);
        setSelectedBookingForReschedule(null);
        onClose();
    };

    const handleOpenAddressLink = (address) => {
        window.open(
            `https://www.google.com/maps/search/?api=1&query=${address.replaceAll(
                " ",
                "+"
            )}`,
            "_blank"
        );
    };

    const handleArrived = async (booking) => {
        //Assume marking the booking as arrived is successful
        //TODO move to useOptimistic hook maybe?
        setSelectedBooking({
            ...selectedBooking,
            status: "ARRIVED"
        });
        updateLocalBooking(booking.id, { status: "ARRIVED" });
        setConfirmationDialogOpen(false);

        const input = {
            id: booking.id,
            timeblockid: booking.timeblockid,
            startdate: booking.startdate,
            status: "ARRIVED"
        };
        try {
            const result = await graphql(
                graphqlOperation(mutations.updateBooking, { input })
            );

            if (result && result.booking) {
                await auditBookingState(mbxUser, booking, "ARRIVED");
            }
        } catch (err) {
            console.log("Error: Unable to mark booking as arrived" + err);
        }

        setConformationDialogIsSubmitting(false);
        return;
    };

    const getBookingsByOrderId = async () => {
        try {
            let result = await graphql(
                graphqlOperation(queries.bookingByOrder, {
                    orderId: selectedBooking.order.id
                })
            );

            let bookingsInOrder = result.data.bookingByOrder.items;
            return bookingsInOrder;
        } catch (error) {
            console.error("Error fetching bookings by order:", error);
            return [];
        }
    };

    const updateBookingsInOrder = async () => {
        try {
            const bookingsToUpdate = await getBookingsByOrderId();

            // Loop through each booking and delete its notes
            for (const booking of bookingsToUpdate) {
                const bookingInput = {
                    id: booking.id,
                    Notes: null
                };

                const bookingResult = await graphql(
                    graphqlOperation(mutations.updateBooking, {
                        input: bookingInput
                    })
                );

                console.log("Updated booking:", bookingResult);
            }

            console.log("All bookings updated successfully.");
        } catch (error) {
            console.error("Error updating bookings:", error);
        }
    };

    const saveBookingNotes = async () => {
        let newNotes = {};
        const date = moment().format("YYYY-MM-DDTHH:mm:ss.SSSZ");

        try {
            if (bookingNotes) {
                //if Notes exists, then only update and use the same createdAt and createdBy attributes
                newNotes = {
                    notes: notes,
                    createdBy: bookingNotes.createdBy,
                    updatedBy: mbxUser.firstname + " " + mbxUser.lastname,
                    createdAt: bookingNotes.createdAt,
                    updatedAt: date
                };
            } else {
                //if Notes dont exists, add new created stamps and update stamps
                newNotes = {
                    notes: notes,
                    createdBy: mbxUser.firstname + " " + mbxUser.lastname,
                    updatedBy: mbxUser.firstname + " " + mbxUser.lastname,
                    createdAt: date,
                    updatedAt: date
                };
            }
            if (applyNotesToOrder) {
                updateBookingsInOrder();

                setSelectedBooking({
                    ...selectedBooking,
                    order: {
                        ...selectedBooking.order,
                        Notes: JSON.stringify([newNotes])
                    }
                });

                const orderInput = {
                    id: selectedBooking.order.id,
                    Notes: JSON.stringify([newNotes])
                };
                const result = await graphql(
                    graphqlOperation(mutations.updateOrder, {
                        input: orderInput
                    })
                );

                if (result && result.data && result.data.updateOrder) {
                    try {
                        await auditOrderNotesUpdate(
                            mbxUser,
                            result.data.updateOrder,
                            selectedBooking.id,
                            bookingNotes,
                            originalBookingNotes,
                            "ORDER_NOTES"
                        );
                    } catch (e) {
                        console.log("Error while entering audit");
                    }
                }
                onSaveNotes(
                    applyNotesToOrder,
                    selectedBooking.order.id,
                    JSON.stringify([newNotes])
                );
                setBookingPageSnackbar({
                    open: true,
                    severity: "success",
                    message: `Notes have been saved.`
                });
            } else {
                setSelectedBooking({
                    ...selectedBooking,
                    Notes: [newNotes],
                    updatedAt: date
                });
                const bookingInput = {
                    id: selectedBooking.id,
                    Notes: JSON.stringify([newNotes])
                };

                const result = await graphql(
                    graphqlOperation(mutations.updateBooking, {
                        input: bookingInput
                    })
                );

                if (result && result.data && result.data.updateBooking) {
                    console.log("Successfully updated booking notes");
                }
                onSaveNotes(applyNotesToOrder, selectedBooking.id, [newNotes]);
                setBookingPageSnackbar({
                    open: true,
                    message: `Notes have been saved.`,
                    severity: "success"
                });
            }
        } catch (e) {
            console.log("ERROR: Unable to save Booking notes ", e);
        }
    };

    const renderBookingIcon = (orderType) => {
        const iconProps = {
            sx: {
                marginLeft: "9px",
                fontSize: "1rem",
                color: theme.palette.primary.main
            }
        };

        switch (orderType) {
            case "ONGOING":
                return <SyncIcon {...iconProps} />;
            case "SINGLE":
                return <LooksOneOutlinedIcon {...iconProps} />;
            default:
                return <PackageIcon {...iconProps} />;
        }
    };

    const renderBookingStatusCTA = (selectedBooking) => {
        const ctaStyles = {
            marginTop: "18px",
            width: "100%"
        };

        const handleArrivedClick = () => {
            setConfirmationFunction(() => handleArrived(selectedBooking));
            setConfirmationDialogContent(
                "Are you sure you want to mark this booking as Arrived?"
            );
            setConformationDialogTitle("Mark as arrived?");
            setConformationDialogSubmitButton("Confirm");
            setConfirmationDialogOpen(true);
        };

        switch (selectedBooking.status) {
            case "SCHEDULED":
                return (
                    <Button
                        variant="outlined"
                        sx={{
                            ...ctaStyles,
                            color: theme.palette.primary.main,
                            borderColor: theme.palette.grey[400]
                        }}
                        onClick={handleArrivedClick}
                    >
                        Tap to mark Arrived
                    </Button>
                );

            case "ARRIVED":
                return (
                    <Button
                        variant="outlined"
                        sx={{
                            ...ctaStyles,
                            color: theme.palette.success.main,
                            border: `1px solid ${theme.palette.success.main}`,
                            "&:hover": {
                                backgroundColor: theme.palette.success.light,
                                border: `1px solid ${theme.palette.success.main}`
                            }
                        }}
                        onClick={() => handleCompleted(selectedBooking)}
                    >
                        Tap to mark Completed
                    </Button>
                );

            case "COMPLETED":
                return (
                    <Button
                        variant="contained"
                        sx={{
                            ...ctaStyles,
                            color: "white",
                            backgroundColor: theme.palette.success.main,
                            border: `1px solid ${theme.palette.success.main}`,
                            "&:hover": {
                                backgroundColor: theme.palette.success.main,
                                border: `1px solid ${theme.palette.success.main}`,
                                color: "white"
                            }
                        }}
                        startIcon={
                            <CheckCircleOutlineIcon sx={{ color: "white" }} />
                        }
                    >
                        Completed
                    </Button>
                );

            case "CANCELLED":
                return (
                    <Button variant="contained" sx={ctaStyles} disabled>
                        Cancelled
                    </Button>
                );

            case "NOSHOW":
                return (
                    <Button
                        variant="contained"
                        sx={{
                            ...ctaStyles,
                            background: theme.palette.caution.light,
                            color: theme.palette.caution.main
                        }}
                    >
                        No show
                    </Button>
                );

            default:
                return null;
        }
    };

    return (
        <>
            <NotificationSnackbar
                open={openSnackbar}
                severity={snackSeverity}
                message={snackMsg}
                onClose={() =>
                    setBookingPageSnackbar({
                        open: false,
                        severity: "success",
                        message: ""
                    })
                }
            />
            <Dialog
                open={open}
                onClose={handleClose}
                TransitionComponent={TransitionComponent}
                fullWidth
                maxWidth="lg"
                fullScreen={!isDesktop}
            >
                <DialogTitle sx={{ gap: "1.125rem" }}>
                    <ArrowIcon
                        sx={{
                            fontSize: "18px",
                            ":hover": {
                                cursor: "pointer"
                            }
                        }}
                        onClick={onClose}
                    />
                    {`Order #${selectedBooking?.orderNumber}`}
                    {selectedBooking?.status === "ARRIVED" && (
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.success.main,
                                fontWeight: "500",
                                lineHeight: "normal"
                            }}
                        >
                            IN PROGRESS
                        </Typography>
                    )}
                </DialogTitle>

                <DialogContent>
                    <Grid
                        container
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "9px",
                            alignItems: "flex-start"
                        }}
                    >
                        <Typography variant="subtitle2">
                            {selectedBooking.clientName}
                        </Typography>
                        <Typography variant="body2">
                            {moment(selectedBooking.bookingDate).format(
                                "ddd, MMM D, YYYY"
                            )}
                        </Typography>
                        <Typography variant="body2">
                            {selectedBooking?.bookingTimeDesc
                                ? selectedBooking.bookingTimeDesc
                                : `${moment
                                      .tz(
                                          selectedBooking?.startdate,
                                          selectedBooking?.timezone
                                      )
                                      .format("h:mm A")} \u2014 ${moment
                                      .tz(
                                          selectedBooking?.startdate,
                                          selectedBooking?.timezone
                                      )
                                      .add(
                                          selectedBooking?.servicetype?.minutes,
                                          "minutes"
                                      )
                                      .format("h:mm A")}`}
                        </Typography>
                        <Typography
                            variant="link"
                            width={"fit-content"}
                            onClick={(e) => {
                                e.stopPropagation();
                                selectedBooking.location.includes(
                                    "Virtual Appointment"
                                )
                                    ? handleOpenVirtualBookingModal(
                                          selectedBooking
                                      )
                                    : handleOpenAddressLink(
                                          selectedBooking.location
                                      );
                            }}
                        >
                            {selectedBooking.location}
                        </Typography>
                        <Typography variant="body2">
                            {selectedBooking.serviceName}
                            {renderBookingIcon(selectedBooking.orderType)}
                        </Typography>
                        {isVerifiedStaff && (
                            <Typography variant="body2">
                                {`With: ${selectedBooking?.provider?.firstname} ${selectedBooking?.provider?.lastname}`}
                            </Typography>
                        )}

                        {renderBookingStatusCTA(selectedBooking)}

                        <LinkDetectionTextField
                            label="Booking notes"
                            labelWidth="80px"
                            value={notes}
                            onChange={(newNotes) => setNotes(newNotes)}
                            shouldAutofocus={false}
                        />

                        <Grid item sx={{ marginTop: "18px" }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={applyNotesToOrder}
                                        onChange={() =>
                                            setApplyNotesToOrder(
                                                !applyNotesToOrder
                                            )
                                        }
                                        name="bookingNotes"
                                    />
                                }
                                label="Apply to each session in this order and replace existing notes"
                            />
                        </Grid>

                        <Grid
                            container
                            flexDirection={"row"}
                            justifyContent={"end"}
                            sx={{ marginTop: "18px" }}
                        >
                            {bookingNotesContent?.updatedBy && (
                                <Grid container item xs={10}>
                                    <Typography
                                        sx={{
                                            fontSize: "12px",
                                            fontStyle: "italic",
                                            color: theme.palette.helperText
                                                .main,
                                            position: "relative",
                                            bottom: "4px"
                                        }}
                                    >
                                        Last edited by{" "}
                                        {bookingNotesContent?.updatedBy} on{" "}
                                        {moment(
                                            bookingNotesContent?.updatedAt
                                        ).format("YYYY-MM-DD [at] HH:mm")}
                                    </Typography>
                                </Grid>
                            )}
                            <Grid container item xs={2} justifyContent={"end"}>
                                <Button
                                    variant="text"
                                    onClick={(e) => {
                                        saveBookingNotes();
                                    }}
                                >
                                    Save
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions
                    sx={{ display: "flex", justifyContent: "space-between" }}
                >
                    <Grid display={"flex"} width={"100%"} gap={"18px"}>
                        {selectedBooking.status === "SCHEDULED" &&
                            userCanCancelAppt() &&
                            (moment(selectedBooking.startdate).isAfter(
                                moment()
                            ) ? (
                                <Button
                                    variant="outlined"
                                    sx={{
                                        width: "100%",
                                        lineHeight: "16px"
                                    }}
                                    onClick={() =>
                                        handleCancel(selectedBooking)
                                    }
                                >
                                    Cancel Booking
                                </Button>
                            ) : (
                                <Button
                                    variant="outlined"
                                    sx={{
                                        width: "100%",
                                        lineHeight: "16px"
                                    }}
                                    onClick={() => {
                                        handleNoShow(selectedBooking);
                                    }}
                                >
                                    No Show
                                </Button>
                            ))}
                        {selectedBooking.status === "SCHEDULED" &&
                            (mbxUser.company.providerCanReschedule ||
                                !isProviderLoggedIn) && (
                                <Button
                                    variant="contained"
                                    sx={{
                                        width: "100%"
                                    }}
                                    onClick={() => {
                                        if (isProviderLoggedIn) {
                                            setSelectedBookingForReschedule(
                                                selectedBooking
                                            );
                                            openRescheduleModal();
                                        } else {
                                            handleOverride(selectedBooking);
                                        }
                                    }}
                                >
                                    Reschedule
                                </Button>
                            )}
                        {selectedBooking.status === "ARRIVED" && (
                            <Button
                                variant="contained"
                                sx={{
                                    width: "100%"
                                }}
                                onClick={() => {
                                    handleAddCharge(selectedBooking);
                                }}
                            >
                                Add Payment
                            </Button>
                        )}
                    </Grid>
                </DialogActions>
            </Dialog>
        </>
    );
}
